import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { OrangeTextButton } from "./OrangeTextButton";

/**
 * In-app replacement for the native `window.alert()`.
 *
 * The native dialog is prefixed with "config.actuateui.net says" by the
 * browser — that header cannot be removed or styled, so `AppAlertHost`
 * (mounted once at the app root) overrides `window.alert` and renders the
 * message in a themed MUI dialog instead. All ~200 existing `alert(...)`
 * call sites get the new look with no per-site changes, and future calls
 * are covered automatically.
 *
 * Semantics: the native alert blocks the thread; this one doesn't. No call
 * site in this codebase branches on alert() returning (verified: zero
 * `confirm()` usage, alerts are fire-and-forget error/info notices), and
 * the dialog is mounted at the root so it survives route changes fired
 * right after an alert. Multiple alerts queue and display one at a time.
 *
 * Jest note: tests that stub `window.alert = jest.fn()` are unaffected —
 * they assign after (or without) mounting this host.
 */
export const AppAlertHost = () => {
  const [queue, setQueue] = React.useState<string[]>([]);

  React.useEffect(() => {
    const nativeAlert = window.alert;
    window.alert = (message?: unknown) => {
      setQueue((prev) => [...prev, String(message ?? "")]);
    };
    return () => {
      window.alert = nativeAlert;
    };
  }, []);

  const current = queue[0];
  const dismiss = () => setQueue((prev) => prev.slice(1));

  return (
    <Dialog open={current !== undefined} onClose={dismiss}>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: "pre-line" }}>
          {current}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <OrangeTextButton onClick={dismiss} text="OK" disabled={false} />
      </DialogActions>
    </Dialog>
  );
};
